<template>
    <div>
        <test-template :testName="testName" :testId="testId" :test="test" :opposedAnswers="opposedAnswers" multidimensional @setFinishedMessageResultsHidden="setMessage">
            <template #questions>
                <question-card :question="cQ" v-model="questionResult" :progress="$store.state.tests.currentSlide" :questionnaireLength="$store.state.tests.test.length"  @answerQuestion="() => { $store.commit('tests/incrementSlide') }" @submitQuestionnaire="saveTestResults"></question-card>
            </template>
            <template #results>
                <b-row align-v="center" v-if=" $store.getters['session/userIsPatient']" class="slidesSize">
                    <loading-anim :active="$store.state.loading" fullScreen></loading-anim>
                    <b-col class="px-0 pt-1 px-md-5 pt-md-2 m-auto align-middle">
                        <h3><strong>{{$t('tests.hello')}} {{ $store.state.session.user.name }},</strong></h3><br>
                        <h5 v-html="psicosocialFinishedMessage"></h5>
                    </b-col>
                </b-row>
                <div v-if="$store.getters['session/userIsProfessional']">
                    <div key="topGraph" class="first">
                        <h3 class="subtitle-card text-center">{{ $t('tests.testResults') }} - {{ testName.toUpperCase() }}</h3>
                        <highcharts :options="chartOptions()"></highcharts>
                    </div>
                    <div key="recapCard" class="second">
                        <h4>{{ $t('tests.results') }} {{ month }}</h4>
                    </div>
                    <div key="dimensions" class="third">
                        <h4>{{ $t('tests.dimensions') }}</h4>
                        <div class="col-lg-12 results-container">
                            <div v-for="d in DBResults.dimensions" :key="d.name" class="col-lg-8 results-card">
                                <b-col>
                                    <b-card class="neu-card" v-b-toggle="d.id" :title="$t(d.name)">
                                        <b-icon-circle-fill :style="interpretationColor(d.result)"></b-icon-circle-fill> <span>{{ interpretation(d.result) }}</span>
                                        <div v-if="d.result === -1" style="margin-top:1%;margin-bottom:-2%">
                                            <span class="text-center accordion-arrow-closed">
                                                <b-icon-chevron-down></b-icon-chevron-down>
                                            </span>
                                            <span class="text-center accordion-arrow-open">
                                                <b-icon-chevron-up></b-icon-chevron-up>
                                            </span>
                                            <b-collapse :id="d.id" class="mt-2 text-left" accordion="accordion">
                                                <p class="card-text text-justify" v-html="$t(d.message)"></p>
                                            </b-collapse>
                                        </div>
                                    </b-card>
                                </b-col>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </test-template>
    </div>
</template>

<script>
import TestTemplate from './TestTemplate.vue'
import { GREEN_COLOUR, YELLOW_COLOUR, RED_COLOUR, BACKGROUND_COLOUR } from '@/constants/colours.js'
import { TEST_PSYCHOSOCIAL_RISKS } from '@/constants/patientServices'
import { BCollapse } from 'bootstrap-vue'
import selfEvaluationTestMixin from '@/mixins/selfEvaluationTest.mixin'
import { formatDateByMonthLong } from '@/helpers/date.helpers'
import { Chart } from 'highcharts-vue'
import Highcharts from 'highcharts'
import Xrange from 'highcharts/modules/xrange.js'
Xrange(Highcharts)

export default {
    mixins: [selfEvaluationTestMixin],
    components: {
        'test-template': TestTemplate,
        BCollapse,
        highcharts: Chart
    },
    data () {
        const values = [
                {value: 0, text: this.$t('tests.answers.never')},
                {value: 1, text: this.$t('tests.answers.justsometimes')},
                {value: 2, text: this.$t('tests.answers.sometimes')},
                {value: 3, text: this.$t('tests.answers.alot')},
                {value: 4, text: this.$t('tests.answers.always')}
        ]

        const worries = [
                {value: 0, text: this.$t('tests.answers.notWorried')},
                {value: 1, text: this.$t('tests.answers.littleWorried')},
                {value: 2, text: this.$t('tests.answers.moreorlessWorried')},
                {value: 3, text: this.$t('tests.answers.prettyWorried')},
                {value: 4, text: this.$t('tests.answers.worried')}
        ]

        const resp = [
            {value: 0, text: this.$t('tests.answers.notResponsible')},
            {value: 1, text: this.$t('tests.answers.punctuallyResponsible')},
            {value: 2, text: this.$t('tests.answers.quarterResponsible')},
            {value: 3, text: this.$t('tests.answers.halfResponsible')},
            {value: 4, text: this.$t('tests.answers.mainResponsible')}
        ]

        const test = [
            {
                dimension: 'tests.psychosocial-risks.dimensions.ap1',
                lowScore: 'tests.psychosocial-risks.scores.psicoDemandsLow',
                qs: ['q1', 'q2', 'q3', 'q4', 'q5', 'q6'],
                interpretations: {
                    positive: {
                        min: 0,
                        max: 7
                    },
                    middle: {
                        min: 8,
                        max: 11
                    },
                    negative: {
                        min: 12,
                        max: 24
                    }
                }
            },
            {
                dimension: 'tests.psychosocial-risks.dimensions.ap2',
                qs: ['q7', 'q8', 'q9', 'q10', 'q11', 'q12', 'q13', 'q14', 'q15', 'q16'],
                lowScore: 'tests.psychosocial-risks.scores.controlOverWorkLow',
                interpretations: {
                    positive: {
                        min: 26,
                        max: 40
                    },
                    middle: {
                        min: 19,
                        max: 25
                    },
                    negative: {
                        min: 0,
                        max: 18
                    }
                }
            },
            {
                dimension: 'tests.psychosocial-risks.dimensions.ap3',
                lowScore: 'tests.psychosocial-risks.scores.insecuritiesLow',
                qs: ['q17', 'q18', 'q19', 'q20'],
                interpretations: {
                    positive: {
                        min: 0,
                        max: 4
                    },
                    middle: {
                        min: 5,
                        max: 9
                    },
                    negative: {
                        min: 10,
                        max: 16
                    }
                }
            },
            {
                dimension: 'tests.psychosocial-risks.dimensions.ap4',
                lowScore: 'tests.psychosocial-risks.scores.leadershipLow',
                qs: ['q21', 'q22', 'q23', 'q24', 'q25', 'q26', 'q27', 'q28', 'q29', 'q30'],
                interpretations: {
                    positive: {
                        min: 32,
                        max: 40
                    },
                    middle: {
                        min: 25,
                        max: 31
                    },
                    negative: {
                        min: 0,
                        max: 24
                    }
                }
            },
            {
                dimension: 'tests.psychosocial-risks.dimensions.ap5',
                lowScore: 'tests.psychosocial-risks.scores.doublePresenceLow',
                qs: ['q31', 'q32', 'q33', 'q34'],
                interpretations: {
                    positive: {
                        min: 0,
                        max: 2
                    },
                    middle: {
                        min: 3,
                        max: 6
                    },
                    negative: {
                        min: 7,
                        max: 16
                    }
                }
            },
            {
                dimension: 'tests.psychosocial-risks.dimensions.ap6',
                lowScore: 'tests.psychosocial-risks.scores.esteemLow',
                qs: ['q35', 'q36', 'q37', 'q38'],
                interpretations: {
                    positive: {
                        min: 13,
                        max: 16
                    },
                    middle: {
                        min: 10,
                        max: 12
                    },
                    negative: {
                        min: 0,
                        max: 9
                    }
                }
            }
        ]

        const opposedAnswers = ['q3', 'q27', 'q37']
        return {
            test: test,
            testId: TEST_PSYCHOSOCIAL_RISKS,
            values: values,
            worries: worries,
            resp: resp,
            opposedAnswers: opposedAnswers,
            testName: this.$t('tests.psychosocial-risks.title'),
            psicosocialFinishedMessage: this.$t('tests.psychosocial-risks.finalCompletedMessage')
        }
    },
    created () {
        this.createQuestions()
    },
    methods: {
        createQuestions () {
            var i = 1
            this.test.map(dimension => {
                dimension.id = `apartado-${i}`
                var questions = []
                dimension.qs.map(x => questions.push({
                    id: x,
                    question: this.$t(`tests.psychosocial-risks.questions.${x}`),
                    answer: ['q17', 'q18', 'q19', 'q20'].includes(x) ? this.worries : x === 'q31' ? this.resp : this.values,
                    result: null
                }))
                i = i + 1
                this.$set(dimension, 'questions', questions)
            })
        },
        saveTestResults () {
            var dimensions = []
            var risksResult = 0
            for (let dimension of this.test) {
                var dimensionValues = this.calculateMultidimensionalDimPoints(dimension)
                var interpretation = this.collectRisksResults(dimension.interpretations, dimensionValues.sum)
                risksResult = risksResult + interpretation
                var messageKey = interpretation === -1 ? dimension.lowScore : null
                dimensions.push({'result': interpretation, 'points': dimensionValues.sum, 'id': dimension.id, 'name': dimension.dimension, 'message': messageKey})
            }
            this.save(dimensions, risksResult)
        },
        collectRisksResults (i, sum) {
            return sum >= i.positive.min && sum <= i.positive.max ? 1
                : sum >= i.middle.min && sum <= i.middle.max ? 0 : -1
        },
        setMessage () {
            this.psicosocialFinishedMessage = this.$t('tests.psychosocial-risks.finalCompletedMessageAccessAgain')
        },
        interpretation (r) {
            return r === 1 ? this.$t('tests.psychosocial-risks.positive') : r === 0 ? this.$t('tests.psychosocial-risks.middle') : this.$t('tests.psychosocial-risks.negative')
        },
        interpretationColor (r) {
            return r === 1 ? `color: ${GREEN_COLOUR}` : r === 0 ? `color: ${YELLOW_COLOUR}` : `color: ${RED_COLOUR}`
        },
        chartOptions () {
            var _this = this
            const series = this.DBResults.dimensions.map((dimension) => {
                const dataPoints = this.previousTestResults.slice(-12).map((r) => {
                    var dim = r.dimensions.find((d) => d.id === dimension.id)
                    var day = new Date(r.date)
                    var names = this.test.map(d => d.id)
                    return {
                        x: Date.UTC(day.getFullYear(), day.getMonth()),
                        x2: Date.UTC(day.getFullYear(), day.getMonth() + 1),
                        y: names.indexOf(dim.id),
                        color: dim.result === -1 ? RED_COLOUR : dim.result === 0 ? YELLOW_COLOUR : GREEN_COLOUR,
                        date: day,
                        result: dim.result
                    }
                })

                return {
                    name: this.$t(dimension.name),
                    data: dataPoints,
                    showInLegend: false,
                    borderWidth: null,
                    pointWidth: 20
                }
            })

            const xAxisWidth = this.$uiHelpers.isMobile || series[0].data.length >= 3 ? undefined : 400

            return {
                chart: {
                    type: 'xrange',
                    backgroundColor: BACKGROUND_COLOUR,
                    marginLeft: xAxisWidth,
                    marginRight: xAxisWidth
                },
                title: {
                    text: ''
                },
                credits: {
                    enabled: false
                },
                tooltip: {
                    formatter: function () {
                        return `<b>${formatDateByMonthLong(this.point.date).replace(/^\w/, c => c.toUpperCase())}</b><br>
                                ${_this.interpretation(this.point.result)}`
                    }
                },
                xAxis: {
                    type: 'datetime',
                    tickInterval: 31 * 24 * 3600 * 1000,
                    labels: {
                        formatter: function () {
                            return new Date(this.value).toLocaleDateString(_this.$i18nHelpers.getCulture(), {month: 'short'})
                        }
                    }
                },
                yAxis: {
                    categories: this.test.map(d => this.$t(d.dimension)),
                    title: null
                },
                series: series
            }
        }
    }
}
</script>


// WEBPACK FOOTER //
// src/components/views/selfEvaluationTests/PsicosocialRisksTest.vue